import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "@/pages/dashboard/components/DashboardLayout";
import { supabase } from "@/lib/supabase";

// ── Stripe type declarations ──────────────────────────────────────────────────
declare global {
  interface Window {
    Stripe?: (key: string) => StripeInstance;
  }
}
interface StripeInstance {
  elements: (options: Record<string, unknown>) => StripeElements;
  confirmSetup: (options: Record<string, unknown>) => Promise<{ error?: { message: string } }>;
  retrieveSetupIntent: (cs: string) => Promise<{
    setupIntent?: { status: string; payment_method?: string };
    error?: { message: string };
  }>;
}
interface StripeElements {
  create: (type: string, options?: Record<string, unknown>) => StripeElement;
  submit: () => Promise<{ error?: { message: string } }>;
}
interface StripeElement {
  mount: (el: HTMLElement) => void;
  destroy: () => void;
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface Purchase {
  id: string;
  lead_id: string;
  amount: number;
  status: string;
  stripe_session_id: string | null;
  created_at: string;
  leads: { city: string; state: string } | null;
}

interface PaymentMethod {
  id: string;
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
  funding: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function loadStripeScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById("stripe-js")) { resolve(); return; }
    const script = document.createElement("script");
    script.id = "stripe-js";
    script.src = "https://js.stripe.com/v3/";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Stripe.js"));
    document.body.appendChild(script);
  });
}

const BRAND_ICONS: Record<string, string> = {
  visa: "ri-visa-line",
  mastercard: "ri-mastercard-line",
  amex: "ri-bank-card-2-line",
  discover: "ri-bank-card-line",
};

function cardBrandIcon(brand: string | null) {
  return BRAND_ICONS[brand?.toLowerCase() ?? ""] ?? "ri-bank-card-line";
}

function cardBrandLabel(brand: string | null) {
  if (!brand) return "Card";
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

// ── Update Card Modal ─────────────────────────────────────────────────────────
function UpdateCardModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [phase, setPhase] = useState<"loading" | "form" | "submitting" | "error">("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const paymentElementRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<StripeInstance | null>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const elementInstanceRef = useRef<StripeElement | null>(null);

  // Load Stripe + create Setup Intent
  useEffect(() => {
    const init = async () => {
      try {
        await loadStripeScript();
        const pubKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
        if (!pubKey) throw new Error("Stripe publishable key not configured");

        const { data: { session } } = await supabase.auth.getSession();
        if (!session) throw new Error("Not authenticated");

        const res = await fetch(
          "https://ukfoujowiwsrrmajzajy.supabase.co/functions/v1/get-payment-method",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
          }
        );
        const data = await res.json();
        if (!res.ok || !data.client_secret) throw new Error(data.error || "Failed to initialize");

        setClientSecret(data.client_secret);
        setPhase("form");
      } catch (e) {
        setErrorMsg(e instanceof Error ? e.message : "Failed to load payment form");
        setPhase("error");
      }
    };
    init();
  }, []);

  // Mount Stripe Payment Element
  useEffect(() => {
    if (phase !== "form" || !clientSecret || !paymentElementRef.current) return;
    const pubKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    if (!pubKey || !window.Stripe) return;

    if (elementInstanceRef.current) {
      elementInstanceRef.current.destroy();
      elementInstanceRef.current = null;
    }

    const stripe = window.Stripe(pubKey);
    stripeRef.current = stripe;
    const elements = stripe.elements({
      clientSecret,
      appearance: {
        theme: "stripe",
        variables: {
          colorPrimary: "#16a34a",
          colorBackground: "#ffffff",
          colorText: "#374151",
          colorDanger: "#ef4444",
          fontFamily: "Inter, system-ui, sans-serif",
          borderRadius: "8px",
        },
      },
    });
    elementsRef.current = elements;
    const el = elements.create("payment", {
      layout: "tabs",
      defaultValues: { billingDetails: { address: { country: "US" } } },
    });
    elementInstanceRef.current = el;
    el.mount(paymentElementRef.current);

    return () => { el.destroy(); elementInstanceRef.current = null; };
  }, [phase, clientSecret]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripeRef.current || !elementsRef.current) return;
    setPhase("submitting");
    setErrorMsg("");

    try {
      const { error: submitError } = await elementsRef.current.submit();
      if (submitError) throw new Error(submitError.message);

      const { error } = await stripeRef.current.confirmSetup({
        elements: elementsRef.current,
        confirmParams: {
          return_url: `${window.location.origin}/dashboard/billing?update_card=success`,
        },
      });
      if (error) throw new Error(error.message);
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Something went wrong");
      setPhase("form");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Update Payment Method</h3>
            <p className="text-xs text-gray-400 mt-0.5">Your new card will replace the existing one</p>
          </div>
          {phase !== "submitting" && (
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
              <i className="ri-close-line text-xl"></i>
            </button>
          )}
        </div>

        {phase === "loading" && (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400">
            <i className="ri-loader-4-line animate-spin text-2xl text-[#16a34a] mb-2"></i>
            <span className="text-sm">Loading secure form...</span>
          </div>
        )}

        {phase === "error" && (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-start gap-2">
              <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
              <span>{errorMsg}</span>
            </div>
            <button onClick={onClose} className="w-full border border-gray-200 text-gray-600 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition cursor-pointer whitespace-nowrap">
              Close
            </button>
          </div>
        )}

        {(phase === "form" || phase === "submitting") && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-xs text-gray-600">
              <i className="ri-lock-line text-[#16a34a]"></i>
              <span>Secured by <strong>Stripe</strong> · PCI compliant</span>
            </div>

            {errorMsg && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-700 flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
                <span>{errorMsg}</span>
              </div>
            )}

            <div
              ref={paymentElementRef}
              className="w-full rounded-lg border border-gray-200 bg-white min-h-[120px] p-1"
            />

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                disabled={phase === "submitting"}
                className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 transition cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={phase === "submitting" || !clientSecret}
                className="flex-1 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white py-2.5 rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap"
              >
                {phase === "submitting" ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i> Saving...
                  </span>
                ) : (
                  <><i className="ri-save-line mr-1.5"></i>Save New Card</>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Payment Method Card ───────────────────────────────────────────────────────
function PaymentMethodCard({ onUpdate }: { onUpdate: () => void }) {
  const [pm, setPm] = useState<PaymentMethod | null>(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const fetchPm = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch(
        "https://ukfoujowiwsrrmajzajy.supabase.co/functions/v1/get-payment-method",
        {
          method: "GET",
          headers: { Authorization: `Bearer ${session.access_token}` },
        }
      );
      const data = await res.json();
      setPm(data.payment_method ?? null);
    } catch {
      setPm(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPm(); }, [fetchPm]);

  const handleSuccess = () => {
    setShowModal(false);
    setSuccessMsg("Payment method updated successfully!");
    fetchPm();
    onUpdate();
    setTimeout(() => setSuccessMsg(""), 4000);
  };

  return (
    <>
      <div className="bg-white border border-gray-100 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className="text-base font-bold text-gray-900">Payment Method</h2>
            <p className="text-xs text-gray-400 mt-0.5">Used for all lead purchases</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-1.5 text-sm font-medium text-[#16a34a] hover:text-[#15803d] transition cursor-pointer whitespace-nowrap border border-green-200 bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-lg"
          >
            <i className="ri-edit-line text-sm"></i>
            {pm ? "Update Card" : "Add Card"}
          </button>
        </div>

        {successMsg && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-700 flex items-center gap-2">
            <i className="ri-checkbox-circle-line"></i>
            {successMsg}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-3 py-4">
            <div className="w-12 h-8 bg-gray-100 rounded-md animate-pulse"></div>
            <div className="space-y-1.5">
              <div className="h-3 bg-gray-100 rounded w-32 animate-pulse"></div>
              <div className="h-3 bg-gray-100 rounded w-20 animate-pulse"></div>
            </div>
          </div>
        ) : pm ? (
          <div className="flex items-center gap-4">
            {/* Card art */}
            <div className="w-14 h-10 bg-gradient-to-br from-gray-800 to-gray-600 rounded-lg flex items-center justify-center shrink-0">
              <i className={`${cardBrandIcon(pm.brand)} text-white text-xl`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900">
                {cardBrandLabel(pm.brand)} ending in {pm.last4}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Expires {pm.exp_month?.toString().padStart(2, "0")}/{pm.exp_year}
                {pm.funding && (
                  <span className="ml-2 capitalize text-gray-300">· {pm.funding}</span>
                )}
              </p>
            </div>
            <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2.5 py-1 rounded-full text-xs font-semibold shrink-0">
              <i className="ri-check-line"></i> Active
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-4 py-2">
            <div className="w-14 h-10 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
              <i className="ri-bank-card-line text-gray-300 text-xl"></i>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">No payment method saved</p>
              <p className="text-xs text-gray-400 mt-0.5">Add a card to start purchasing leads</p>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="ml-auto bg-[#16a34a] hover:bg-[#15803d] text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition cursor-pointer whitespace-nowrap"
            >
              Add Card
            </button>
          </div>
        )}

        <div className="mt-5 pt-4 border-t border-gray-50 flex items-center gap-2 text-xs text-gray-400">
          <i className="ri-shield-check-line text-[#16a34a]"></i>
          <span>Card details are stored securely by Stripe. We never see your full card number.</span>
        </div>
      </div>

      {showModal && (
        <UpdateCardModal
          onClose={() => setShowModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </>
  );
}

// ── Webhook Setup Banner ──────────────────────────────────────────────────────
function WebhookSetupBanner() {
  const [dismissed, setDismissed] = useState(() =>
    localStorage.getItem("webhook_banner_dismissed") === "true"
  );

  if (dismissed) return null;

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-settings-3-line text-amber-600 text-lg"></i>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900 mb-1">Complete Stripe Webhook Setup</p>
            <p className="text-xs text-amber-700 leading-relaxed mb-3">
              To automatically mark leads as sold after payment, add this webhook URL in your Stripe Dashboard:
            </p>
            <div className="bg-white border border-amber-200 rounded-lg px-3 py-2 font-mono text-xs text-gray-700 break-all select-all mb-3">
              https://ukfoujowiwsrrmajzajy.supabase.co/functions/v1/stripe-webhook
            </div>
            <div className="space-y-1.5 text-xs text-amber-700">
              <p className="font-semibold text-amber-800">Steps:</p>
              <ol className="list-decimal list-inside space-y-1 ml-1">
                <li>Go to <strong>Stripe Dashboard → Developers → Webhooks</strong></li>
                <li>Click <strong>"Add endpoint"</strong> and paste the URL above</li>
                <li>Select events: <code className="bg-amber-100 px-1 rounded">checkout.session.completed</code>, <code className="bg-amber-100 px-1 rounded">checkout.session.expired</code>, <code className="bg-amber-100 px-1 rounded">setup_intent.succeeded</code></li>
                <li>Copy the <strong>Signing Secret</strong> (starts with <code className="bg-amber-100 px-1 rounded">whsec_</code>)</li>
                <li>Add it to <strong>Supabase → Edge Functions → Secrets</strong> as <code className="bg-amber-100 px-1 rounded">STRIPE_WEBHOOK_SECRET</code></li>
              </ol>
            </div>
            <div className="flex gap-2 mt-4">
              <a
                href="https://dashboard.stripe.com/webhooks"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line"></i>
                Open Stripe Dashboard
              </a>
              <a
                href="https://supabase.com/dashboard/project/ukfoujowiwsrrmajzajy/functions"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 border border-amber-300 text-amber-700 hover:bg-amber-100 text-xs font-semibold px-3 py-1.5 rounded-lg transition cursor-pointer whitespace-nowrap"
              >
                <i className="ri-external-link-line"></i>
                Open Supabase Secrets
              </a>
            </div>
          </div>
        </div>
        <button
          onClick={() => {
            localStorage.setItem("webhook_banner_dismissed", "true");
            setDismissed(true);
          }}
          className="text-amber-400 hover:text-amber-600 cursor-pointer shrink-0 mt-0.5"
          title="Dismiss"
        >
          <i className="ri-close-line text-lg"></i>
        </button>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function BillingPage() {
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Handle return from Stripe update-card redirect
  const updateCardStatus = searchParams.get("update_card");
  const setupIntentSecret = searchParams.get("setup_intent_client_secret");
  const [updateMsg, setUpdateMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    if (updateCardStatus === "success" && setupIntentSecret) {
      // Confirm the setup intent and save the payment method
      const confirm = async () => {
        try {
          await loadStripeScript();
          const pubKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
          if (!pubKey || !window.Stripe) return;

          const stripe = window.Stripe(pubKey);
          const { setupIntent, error } = await stripe.retrieveSetupIntent(setupIntentSecret);

          if (error) throw new Error(error.message);

          if (setupIntent?.status === "succeeded" && setupIntent.payment_method) {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              await supabase
                .from("installers")
                .update({ stripe_payment_method_id: setupIntent.payment_method })
                .eq("id", session.user.id);
            }
            setUpdateMsg({ type: "success", text: "Payment method updated successfully!" });
          }
        } catch (e) {
          setUpdateMsg({ type: "error", text: e instanceof Error ? e.message : "Update failed" });
        } finally {
          // Clean up URL params
          setSearchParams({}, { replace: true });
        }
      };
      confirm();
    }
  }, [updateCardStatus, setupIntentSecret, setSearchParams]);

  const fetchPurchases = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setError("Not authenticated"); setLoading(false); return; }

      const { data, error: fetchError } = await supabase
        .from("lead_purchases")
        .select("id, lead_id, amount, status, stripe_session_id, created_at, leads(city, state)")
        .eq("installer_id", user.id)
        .eq("status", "completed")
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;
      setPurchases((data as Purchase[]) || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPurchases(); }, [fetchPurchases]);

  const totalSpent = purchases.reduce((sum, p) => sum + Number(p.amount), 0);
  const avgCost = purchases.length > 0 ? Math.round(totalSpent / purchases.length) : 0;

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

  const statusColor = (s: string) =>
    s === "completed" ? "bg-green-100 text-green-700" :
    s === "pending" ? "bg-yellow-100 text-yellow-700" :
    "bg-red-100 text-red-700";

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing</h1>
          <p className="text-gray-500 text-sm mt-1">Manage your payment method and view transaction history</p>
        </div>

        {/* Update card return message */}
        {updateMsg && (
          <div className={`rounded-xl px-5 py-4 flex items-center gap-3 text-sm ${
            updateMsg.type === "success"
              ? "bg-green-50 border border-green-200 text-green-800"
              : "bg-red-50 border border-red-200 text-red-800"
          }`}>
            <i className={updateMsg.type === "success" ? "ri-checkbox-circle-line text-lg" : "ri-error-warning-line text-lg"}></i>
            {updateMsg.text}
            <button onClick={() => setUpdateMsg(null)} className="ml-auto cursor-pointer text-current opacity-60 hover:opacity-100">
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}

        {/* Webhook setup banner */}
        <WebhookSetupBanner />

        {/* Payment Method Card */}
        <PaymentMethodCard onUpdate={fetchPurchases} />

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: "Total Spent", value: loading ? "—" : `$${totalSpent.toLocaleString()}`, icon: "ri-money-dollar-circle-line", color: "text-[#16a34a]" },
            { label: "Leads Purchased", value: loading ? "—" : String(purchases.length), icon: "ri-shopping-bag-line", color: "text-gray-900" },
            { label: "Avg. Cost / Lead", value: loading ? "—" : purchases.length > 0 ? `$${avgCost}` : "—", icon: "ri-bar-chart-line", color: "text-gray-900" },
          ].map(s => (
            <div key={s.label} className="bg-white border border-gray-100 rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 bg-gray-50 rounded-lg flex items-center justify-center">
                  <i className={`${s.icon} text-lg text-gray-600`}></i>
                </div>
                <span className="text-sm text-gray-500">{s.label}</span>
              </div>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Transaction History */}
        <div className="bg-white border border-gray-100 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-bold text-gray-900">Transaction History</h2>
            {!loading && (
              <button
                onClick={fetchPurchases}
                className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 cursor-pointer"
              >
                <i className="ri-refresh-line"></i> Refresh
              </button>
            )}
          </div>

          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-[#16a34a] border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-3 text-sm text-gray-400">Loading transactions...</span>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-4 text-red-700 text-sm flex items-center gap-2">
              <i className="ri-error-warning-line"></i> {error}
            </div>
          )}

          {!loading && !error && purchases.length === 0 && (
            <div className="text-center py-12">
              <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-receipt-line text-xl text-gray-300"></i>
              </div>
              <p className="text-sm text-gray-400">No transactions yet</p>
              <p className="text-xs text-gray-300 mt-1">Your lead purchases will appear here</p>
            </div>
          )}

          {!loading && !error && purchases.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left text-xs text-gray-400 font-medium pb-3">Date</th>
                    <th className="text-left text-xs text-gray-400 font-medium pb-3">Description</th>
                    <th className="text-left text-xs text-gray-400 font-medium pb-3 hidden sm:table-cell">Session ID</th>
                    <th className="text-left text-xs text-gray-400 font-medium pb-3">Status</th>
                    <th className="text-right text-xs text-gray-400 font-medium pb-3">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {purchases.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50/50 transition">
                      <td className="py-3 text-gray-500 whitespace-nowrap">{formatDate(p.created_at)}</td>
                      <td className="py-3 text-gray-900 font-medium">
                        {p.leads ? `Lead Purchase — ${p.leads.city}, ${p.leads.state}` : "Lead Purchase"}
                      </td>
                      <td className="py-3 text-gray-400 text-xs font-mono hidden sm:table-cell">
                        {p.stripe_session_id ? `${p.stripe_session_id.slice(0, 20)}…` : "—"}
                      </td>
                      <td className="py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${statusColor(p.status)}`}>
                          {p.status}
                        </span>
                      </td>
                      <td className="py-3 text-right font-bold text-gray-900">
                        ${Number(p.amount).toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-gray-100">
                    <td colSpan={3} className="pt-3 text-xs text-gray-400 hidden sm:table-cell"></td>
                    <td className="pt-3 text-xs font-semibold text-gray-500">Total</td>
                    <td className="pt-3 text-right font-bold text-gray-900">${totalSpent.toLocaleString("en-US", { minimumFractionDigits: 2 })}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
